import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ResizableDialogContent from "@/components/ResizableDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash, LinkSimple, Copy, Plus, CheckCircle, XCircle, Clock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShareLinksDialog — gestione dei link condivisi di un report o di una simulazione.
 *
 * Props:
 *  - open, onOpenChange
 *  - resource: { id, type: 'report' | 'simulation', name }
 *
 * B80 P1.2: elenco, revoca, creazione con scadenza.
 */
export default function ShareLinksDialog({ open, onOpenChange, resource }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiresDays, setExpiresDays] = useState(30);
  // B82 P2.2 — modale custom di conferma revoca (stile Console Admin).
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeAcknowledged, setRevokeAcknowledged] = useState(false);

  const baseUrl = resource?.type === "simulation"
    ? `${API}/simulations/${resource?.id}/share`
    : `${API}/reports/${resource?.id}/share`;
  const publicPathPrefix = resource?.type === "simulation" ? "/shared-simulation" : "/shared-report";

  const load = useCallback(async () => {
    if (!open || !resource?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(baseUrl, { withCredentials: true });
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Impossibile caricare i link condivisi");
    } finally {
      setLoading(false);
    }
  }, [open, resource?.id, baseUrl]);

  useEffect(() => { load(); }, [load]);

  const createLink = async () => {
    setCreating(true);
    try {
      const payload = { expires_days: expiresDays ? parseInt(expiresDays, 10) : null };
      const { data } = await axios.post(baseUrl, payload, { withCredentials: true });
      const publicUrl = `${window.location.origin}${publicPathPrefix}/${data.share_id}`;
      try { await navigator.clipboard.writeText(publicUrl); } catch { /* clipboard non disponibile */ }
      toast.success(
        expiresDays > 0
          ? `Link creato: scade in ${expiresDays} giorni. Copiato negli appunti.`
          : "Link creato senza scadenza. Copiato negli appunti."
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione link");
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (shareId) => {
    // B82 P2.2 — apre modale custom (niente piu' window.confirm).
    setRevokeTarget(shareId);
    setRevokeAcknowledged(false);
  };

  const confirmRevoke = async () => {
    const shareId = revokeTarget;
    if (!shareId) return;
    try {
      await axios.delete(`${baseUrl}/${shareId}`, { withCredentials: true });
      toast.success("Link revocato");
      setRevokeTarget(null);
      setRevokeAcknowledged(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore revoca");
    }
  };

  const copyLink = async (shareId) => {
    const url = `${window.location.origin}${publicPathPrefix}/${shareId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare (clipboard non disponibile)");
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      // B82 P2.1: tutti i timestamp arrivano dal server in ISO UTC (+00:00);
      // qui li mostriamo nell'ora locale dell'utente in modo esplicito.
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResizableDialogContent defaultW={780} defaultH={600}>
        <DialogHeader>
          <DialogTitle data-testid="share-links-title">
            Link condivisi — {resource?.name || ""}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 p-3 rounded-lg border border-[#0D1F3C]/8 bg-[#F8FAFC]">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-[#0D1F3C]/60 block mb-1">Scadenza (giorni)</label>
              <Input
                type="number" min="0" max="3650" value={expiresDays}
                onChange={(e) => setExpiresDays(e.target.value)}
                placeholder="30 = 30 giorni, 0 = nessuna scadenza"
                data-testid="share-expires-input"
              />
              <p className="text-xs text-[#0D1F3C]/50 mt-1">
                0 o vuoto = senza scadenza. Il link resta attivo fino alla revoca manuale.
              </p>
            </div>
            <Button
              onClick={createLink} disabled={creating}
              className="bg-[#1B4FD8] hover:bg-[#1B4FD8]/90 text-white gap-2"
              data-testid="share-create-btn"
            >
              <Plus size={16} /> Crea link
            </Button>
          </div>
        </div>

        <div className="mt-4">
          {loading ? (
            <div className="text-sm text-[#0D1F3C]/50 py-8 text-center" data-testid="share-links-loading">
              Caricamento...
            </div>
          ) : links.length === 0 ? (
            <div className="text-sm text-[#0D1F3C]/50 py-8 text-center" data-testid="share-links-empty">
              Nessun link condiviso per questa risorsa.
            </div>
          ) : (
            <div className="space-y-2" data-testid="share-links-list">
              {links.map((lk) => (
                <div key={lk.share_id}
                  className={`p-3 rounded-lg border flex items-center gap-3 ${
                    lk.active ? "border-[#1B4FD8]/20 bg-white" : "border-[#0D1F3C]/8 bg-[#F8FAFC] opacity-60"
                  }`}
                  data-testid={`share-link-${lk.share_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <LinkSimple size={14} className="text-[#1B4FD8] shrink-0" />
                      <code className="text-xs text-[#0D1F3C] truncate">{publicPathPrefix}/{lk.share_id}</code>
                      {lk.active ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle size={10} /> Attivo
                        </span>
                      ) : lk.revoked_at ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1">
                          <XCircle size={10} /> Revocato
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                          <Clock size={10} /> Scaduto
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#0D1F3C]/50 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span>Creato: {fmt(lk.created_at)}</span>
                      <span>Scadenza: {lk.expires_at ? fmt(lk.expires_at) : "nessuna"}</span>
                      <span>Ultimo accesso: {fmt(lk.last_accessed_at)}</span>
                      <span>Aperture: {lk.access_count || 0}</span>
                      {lk.revoked_at && <span className="text-red-600">Revocato: {fmt(lk.revoked_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {lk.active && (
                      <Button variant="ghost" size="sm" onClick={() => copyLink(lk.share_id)}
                        title="Copia link"
                        data-testid={`share-copy-${lk.share_id}`}>
                        <Copy size={14} />
                      </Button>
                    )}
                    {lk.active && (
                      <Button variant="ghost" size="sm" onClick={() => revokeLink(lk.share_id)}
                        title="Revoca link"
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`share-revoke-${lk.share_id}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ResizableDialogContent>
      {/* B82 P2.2 — modale custom di conferma revoca (stile Console Admin) */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => { if (!o) { setRevokeTarget(null); setRevokeAcknowledged(false); } }}>
        <ResizableDialogContent defaultW={520} defaultH={340} data-testid="revoke-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={22} weight="fill" /> Revocare questo link condiviso?
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3 text-sm text-[#0D1F3C]">
            <p>
              Da questo momento chiunque apra il link vedrà che non è più attivo.
              L'azione è <strong>definitiva</strong> e non può essere annullata:
              se serve dare accesso di nuovo, dovrai creare un nuovo link.
            </p>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
              <p className="font-medium mb-1">Prima di procedere:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Il destinatario non riceverà nessuna notifica automatica.</li>
                <li>Il numero di aperture e la data di ultimo accesso restano in archivio.</li>
                <li>Chi ha scaricato il PDF prima della revoca lo conserva localmente.</li>
              </ul>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer pt-1">
              <Checkbox
                checked={revokeAcknowledged}
                onCheckedChange={(v) => setRevokeAcknowledged(!!v)}
                data-testid="revoke-ack-checkbox"
              />
              <span>Confermo di voler revocare il link in modo definitivo.</span>
            </label>
          </div>
          <DialogFooter className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setRevokeTarget(null); setRevokeAcknowledged(false); }} data-testid="revoke-cancel-btn">
              Annulla
            </Button>
            <Button
              onClick={confirmRevoke}
              disabled={!revokeAcknowledged}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid="revoke-confirm-btn"
            >
              Revoca definitivamente
            </Button>
          </DialogFooter>
        </ResizableDialogContent>
      </Dialog>
    </Dialog>
  );
}
